import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { FieldSet, ValidationResult } from "./Form";
import { Validator, Validators } from "./Validators";
import { t } from "../../i18n";
import { KitField, KitFieldOption, nextKitFieldId, registerKitField, unregisterKitField } from "./kitFieldRegistry";

// The DOM-side value of an option — typically an id the backend assigns.
type OptionValue = string | number | undefined;

interface Option {
  value: OptionValue;
  label: string;
}

interface SearchableSelectInputProps<T> {
  // Injected by the Form (see FormUtils.renderChildren): the fieldSet this input
  // binds to, its model path, the current value, and its validity. The stored value
  // is `unknown` because a consumer's convertOptionValueToModel may map an option to
  // an arbitrary shape (an entity object, an id, a sentinel).
  fieldSet?: FieldSet;
  model?: string;
  modelValue?: unknown;
  valid?: boolean;

  values?: T[];
  displayValue?: (value: T) => string;
  optionModelValue?: (value: T) => OptionValue;
  convertOptionValueToModel?: (value: OptionValue) => unknown;
  convertModelValueToOption?: (value: T) => OptionValue;
  optional?: boolean;
  optionalText?: string;
  placeholder?: string;
  required?: boolean;
  validators?: Validator<unknown>[];
  className?: string;
  errorClass?: string;
  disabled?: boolean;
  testId?: string;
}

// Rendering a thousand-entry menu on open is what makes react-select janky. Until the
// user narrows the list, only the first slice is handed to the menu; the moment they
// type, the full list is searched.
const UNFILTERED_RENDER_CAP = 100;
const requiredValidator = Validators.required<unknown>();

/**
 * A searchable, type-to-filter alternative to {@link SelectInput} for long option
 * lists. It honours the same Form model-binding contract (values / displayValue /
 * optionModelValue / convertOptionValueToModel / convertModelValueToOption), so it
 * drops in wherever a call site uses only those props.
 *
 * It is not a full replacement for {@link SelectInput}: `multiple`, `beNull`,
 * `bigInteger`, `children` and `id` are not supported. It targets single-select long
 * lists; use {@link SelectInput} for multi-select or the other cases.
 */
export function SearchableSelectInput<T>(props: SearchableSelectInputProps<T>): JSX.Element {
  const { fieldSet, model, modelValue, values, optionModelValue, displayValue } = props;
  const [inputValue, setInputValue] = useState("");

  // With no optionModelValue the item itself is the option value; the consumers that
  // omit it treat their items as already-primitive, so the cast documents that contract.
  const toOptionValue = (value: T): OptionValue => (props.optionModelValue ? props.optionModelValue(value) : (value as unknown as OptionValue));

  const firstModelValue = (): unknown => {
    const values = props.values;
    if (!values || values.length === 0) {
      return undefined;
    }
    const optionValue = toOptionValue(values[0]);
    return props.convertOptionValueToModel ? props.convertOptionValueToModel(optionValue) : optionValue;
  };

  const defaultValue = (): unknown => (props.optional ? null : firstModelValue());
  const currentValue = modelValue == null ? defaultValue() : modelValue;

  const validate = (value: unknown, pristine: boolean): ValidationResult => {
    const modelObject = fieldSet ? fieldSet.getModelObject() : undefined;
    if (props.required && !requiredValidator.validate(value, modelObject)) {
      return Validators.error(requiredValidator.getName(), pristine);
    }
    for (const validator of props.validators || []) {
      if (!validator.validate(value, modelObject)) {
        return Validators.error(validator.getName(), pristine);
      }
    }
    return Validators.valid(pristine);
  };

  // The value and validation to register are read from the first render's props and
  // frozen — the form only renders after its data has loaded, so `values` is populated.
  const registration = useRef<{ value: unknown; validation: ValidationResult }>();
  if (!registration.current) {
    const initial = modelValue == null ? defaultValue() : modelValue;
    registration.current = { value: initial, validation: validate(initial, true) };
  }

  // Register the model (and its default) on mount, drop its validation on unmount — the
  // same lifecycle the class inputs get from AbstractInputElement. `fieldSet` (the Form
  // instance) and `model` are stable for the component's life, so this runs once.
  useEffect(() => {
    if (!fieldSet) {
      return;
    }
    const { value, validation } = registration.current!;
    fieldSet.setInitialModel(model, value, true, validation);
    return () => fieldSet.removeValidation(model);
  }, [fieldSet, model]);

  // Mapping the whole (up to 1000-entry) list is the ticket's hot path — typing updates
  // inputValue and would otherwise re-map on every keystroke. Rebuild only when the
  // source list or its option/label extractors change.
  const options: Option[] = useMemo(
    () =>
      (values || []).map((value) => ({
        // With no optionModelValue the item itself is the option value; consumers that
        // omit it treat their items as already-primitive, so the cast documents that contract.
        value: optionModelValue ? optionModelValue(value) : (value as unknown as OptionValue),
        label: displayValue ? displayValue(value) : String(value),
      })),
    [values, optionModelValue, displayValue],
  );

  const selectedOption = (): Option | null => {
    if (currentValue == null) {
      return null;
    }
    // The stored value maps back to an option value the same way it came in. It is `T`
    // for every consumer that supplies convertModelValueToOption (the id extractor).
    const optionValue = props.convertModelValueToOption ? props.convertModelValueToOption(currentValue as T) : (currentValue as OptionValue);
    // Loose compare: option values are ids that may cross number/string on the wire.
    return options.find((option) => option.value == optionValue) || null;
  };

  const visibleOptions = (): Option[] => {
    if (inputValue) {
      return options;
    }
    return options.length > UNFILTERED_RENDER_CAP ? options.slice(0, UNFILTERED_RENDER_CAP) : options;
  };

  const handleChange = (option: Option | null): void => {
    if (!option) {
      const cleared = props.optional ? null : undefined;
      fieldSet && fieldSet.updateModel(model, cleared, false, validate(cleared, false));
      return;
    }
    const value = props.convertOptionValueToModel ? props.convertOptionValueToModel(option.value) : option.value;
    fieldSet && fieldSet.updateModel(model, value, false, validate(value, false));
  };

  const readOnly = fieldSet ? fieldSet.isReadOnly() : false;

  // The registry handle is created once on mount but must answer with this render's
  // options, value and validators, so it reads everything through a ref refreshed per render.
  const fieldIdRef = useRef<string>();
  if (!fieldIdRef.current) {
    fieldIdRef.current = nextKitFieldId();
  }
  const fieldId = fieldIdRef.current;
  const latest = useRef<Pick<KitField, "options" | "currentValue" | "isRequired" | "isReadOnly" | "validate" | "apply">>();
  latest.current = {
    options: (): KitFieldOption[] => options,
    // Option space, like SelectInput: the value an <option> would carry, not the stored model shape.
    currentValue: (): unknown =>
      currentValue == null ? null : props.convertModelValueToOption ? props.convertModelValueToOption(currentValue as T) : currentValue,
    isRequired: (): boolean => props.required === true || (props.validators || []).some((validator) => validator.getName() === requiredValidator.getName()),
    isReadOnly: (): boolean => props.disabled === true || readOnly,
    validate: (value: unknown): ValidationResult => validate(value, false),
    // Loose compare for the same reason as in selectedOption.
    apply: (value: unknown): void => handleChange(options.find((option) => option.value == value) ?? null),
  };

  useEffect(() => {
    if (!fieldSet) {
      return;
    }
    registerKitField({
      fieldId,
      model,
      formId: fieldSet.formId,
      fieldSet,
      kind: () => "searchable-select",
      options: () => latest.current!.options(),
      currentValue: () => latest.current!.currentValue(),
      isRequired: () => latest.current!.isRequired(),
      isReadOnly: () => latest.current!.isReadOnly(),
      validate: (value) => latest.current!.validate(value),
      apply: (value) => latest.current!.apply(value),
    });
    return () => unregisterKitField(fieldId);
  }, [fieldSet, model, fieldId]);

  const placeholder = (): string => {
    if (props.placeholder) {
      return props.placeholder;
    }
    if (props.optionalText) {
      return props.optionalText;
    }
    if (options.length > UNFILTERED_RENDER_CAP) {
      return t("form.searchable-select.type-to-search", undefined, "Type to search…");
    }
    return t("form.searchable-select.search-placeholder", undefined, "Search…");
  };

  const isValid = model && fieldSet ? fieldSet.isModelValid(model) : true;
  // Drop Bootstrap's fixed-height .form-control from the react-select container; keep
  // the fm-<model> hook and any validity class so styling and tests still match.
  let className = props.className || "";
  if (isValid === false) {
    className += ` ${props.errorClass != null ? props.errorClass : "error is-invalid"}`;
  }
  if (model) {
    className += ` fm-${model.replace(".", "_")}`;
  }
  className = className.replace(/\bform-control\b/g, "").trim();

  // react-select renders no host DOM attribute we can key an e2e selector off, so the
  // testId rides on a wrapper. Default it from the model so every instance is anchored
  // even when the call site omits it. The field identity rides on the same wrapper.
  const testId = props.testId || (model ? `searchable-select-${model}` : "searchable-select");

  return (
    <div
      data-testid={testId}
      data-model={model}
      data-field-id={fieldId}
    >
      <Select
        className={className}
        classNamePrefix="searchable-select"
        options={visibleOptions()}
        value={selectedOption()}
        isClearable={props.optional === true}
        isDisabled={props.disabled === true || readOnly}
        placeholder={placeholder()}
        onInputChange={(value) => {
          setInputValue(value);
          return value;
        }}
        onChange={(option) => handleChange(option as Option | null)}
        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      />
    </div>
  );
}
